import PropTypes from 'prop-types';
import React from 'react';
import { Button } from '@lightspeed/flame/Button';
import Group, { GroupAddon } from '@lightspeed/cirrus-group';
import Input from '@lightspeed/cirrus-input';
import Modal, { ModalHeader, ModalBody, ModalFooter } from '@lightspeed/cirrus-modal';
import FormField from 'cirrus/FormField';
import Format from 'app/src/data/Format';
import Decimal from 'app/src/data/base/Decimal';

class AddDepositModal extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      showModal: true,
      amount: '',
      inputError: '',
    };
    this.handleCloseModal = this.handleCloseModal.bind(this);
    this.handleAddDeposit = this.handleAddDeposit.bind(this);
    this.handleDepositAmountChange = this.handleDepositAmountChange.bind(this);
    this.handleKeyPress = this.handleKeyPress.bind(this);
  }

  /**
   * Handles closing the modal.  Since we have closing animations, we need to make
   * sure we wait for that to finish before we unmount otherwise we'll get an
   * exception thrown by React.
   */
  handleCloseModal() {
    this.setState(
      {
        showModal: false,
      },
      () => {
        // In case the animation end was not triggered
        setTimeout(() => {
          this.props.onHide();
        }, 250);
      }
    );
  }

  /**
   * Event handler for when the user clicks on the Add button or hits the
   * enter key from inside the deposit amount input.
   */
  handleAddDeposit() {
    const amount = Format.parseDecimal(this.state.amount, 0);

    if (amount.isZero()) {
      this.setState(prevState => ({
        inputError: `${String(prevState.amount).trim() || 'Deposit amount'} is invalid`,
      }));
    } else if (amount.isNegative()) {
      this.setState({
        inputError: 'The deposit cannot be negative',
      });
    } else {
      this.props.onAddDeposit(Format.parseDecimal(this.state.amount, 0));
      this.handleCloseModal();
    }
  }

  /**
   * Event handler for when the deposit amount changes.
   *
   */
  handleDepositAmountChange(event) {
    this.setState({ amount: event.target.value });
  }

  /**
   * Event handler for when a key has been entered into the deposit amount field.
   * Checks for the detection of and Enter key press.
   */
  handleKeyPress(event) {
    if (event.key === 'Enter') {
      this.handleAddDeposit();
    }
  }

  /**
   * Renders the open orders block
   *
   * @param {Decimal} openOrders The open orders for the customer
   *
   * @return {node}   The rendered open orders block
   */
  renderOpenOrders(openOrders) {
    return (
      <div className="add-deposit-orders add-deposit-item">
        <strong>Open Orders</strong>
        <span className="notranslate">{Format.formatMoney(openOrders, 0)}</span>
      </div>
    );
  }

  /**
   * The React render method.
   *
   * @returns {node}  The rendered AddDepositModal
   */
  render() {
    const { showModal } = this.state;
    const { isAccountPayment, currency, customerName, creditBalance, openOrders } = this.props;
    return (
      <Modal
        className="cirrus responsive add-deposit"
        isOpen={showModal}
        onRequestClose={this.handleCloseModal}
        shouldCloseOnOverlayClick={false}
      >
        <ModalHeader onCloseClicked={this.handleCloseModal} className="add-deposit-header">
          {isAccountPayment ? 'Account Payment' : 'Add Deposit'}
        </ModalHeader>
        <div className="add-deposit-fill-block" />
        <ModalBody className="add-deposit-body">
          <div className="add-deposit-customer add-deposit-item">
            <div className="add-deposit-customer-title">
              <strong>Credit Account Balance</strong>
            </div>
            <div className="add-deposit-customer-name notranslate">{customerName}</div>
            <div>
              <strong className="add-deposit-credit-balance notranslate">
                {Format.formatMoney(creditBalance, 0)}
              </strong>
            </div>
          </div>
          <div className="add-deposit-item">
            <FormField
              label={isAccountPayment ? 'Payment Amount' : 'Deposit Amount'}
              status={
                this.state.inputError ? { type: 'error', message: this.state.inputError } : null
              }
            >
              <Group noSpacing inputBlock>
                <GroupAddon>{currency}</GroupAddon>
                <Input
                  autoFocus
                  id="deposit-amount"
                  value={this.state.amount}
                  placeholder={isAccountPayment ? 'Payment Amount...' : 'Deposit Amount...'}
                  onChange={this.handleDepositAmountChange}
                  onKeyPress={this.handleKeyPress}
                />
              </Group>
            </FormField>
          </div>
          {openOrders.isGreaterThan(0) ? this.renderOpenOrders(openOrders) : null}
        </ModalBody>
        <ModalFooter className="add-deposit-footer">
          <Group className="cr-justify-end">
            <Button id="cancel-deposit" onClick={this.handleCloseModal}>
              Cancel
            </Button>
            <Button data-test="add-deposit" variant="primary" fill onClick={this.handleAddDeposit}>
              Add
            </Button>
          </Group>
        </ModalFooter>
      </Modal>
    );
  }
}

AddDepositModal.propTypes = {
  onAddDeposit: PropTypes.func.isRequired,
  onHide: PropTypes.func.isRequired,
  isAccountPayment: PropTypes.bool,
  currency: PropTypes.string.isRequired,
  customerName: PropTypes.string.isRequired,
  creditBalance: PropTypes.instanceOf(Decimal).isRequired,
  openOrders: PropTypes.instanceOf(Decimal).isRequired,
};

AddDepositModal.defaultProps = {
  isAccountPayment: false,
};

export default AddDepositModal;
